
import React, { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { HistoryTabs } from './HistoryTabs';
import { 
  getSelectionsFromSupabase, 
  getTransfersFromSupabase, 
  getRechargesFromSupabase, 
  getRechargeCardsFromSupabase,
  getAdslTransactionsFromSupabase
} from '@/lib/supabase';
import { useOnboardingContext } from '@/context/OnboardingContext';

interface HistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const HistoryDialog: React.FC<HistoryDialogProps> = ({ open, onOpenChange }) => {
  const [date, setDate] = useState<Date | undefined>(undefined);
  const { t } = useTranslation();
  const { userData } = useOnboardingContext() || { userData: null };
  
  const [selections, setSelections] = useState<any[]>([]);
  const [transfers, setTransfers] = useState<any[]>([]);
  const [recharges, setRecharges] = useState<any[]>([]);
  const [rechargeCards, setRechargeCards] = useState<any[]>([]);
  const [adslTransactions, setAdslTransactions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  useEffect(() => {
    if (open && userData?.email) {
      fetchData();
    }
  }, [date, open, userData?.email]);
  
  const fetchData = async () => {
    if (!userData?.email) {
      console.log('📊 History - No user email, skipping data fetch');
      return;
    }
    
    setIsLoading(true);
    try {
      const userEmail = userData.email;
      console.log('📊 History - Fetching data for user:', userEmail);
      
      const [selectionsData, transfersData, rechargesData, rechargeCardsData, adslTransactionsData] = await Promise.all([
        getSelectionsFromSupabase(date, userEmail),
        getTransfersFromSupabase(date, userEmail),
        getRechargesFromSupabase(date, userEmail),
        getRechargeCardsFromSupabase(date, userEmail),
        getAdslTransactionsFromSupabase(date, userEmail)
      ]);
      
      console.log('📊 History - Data fetched:', {
        selections: selectionsData?.length,
        transfers: transfersData?.length,
        recharges: rechargesData?.length,
        rechargeCards: rechargeCardsData?.length,
        adslTransactions: adslTransactionsData?.length
      });
      
      setSelections(selectionsData || []);
      setTransfers(transfersData || []);
      setRecharges(rechargesData || []);
      setRechargeCards(rechargeCardsData || []);
      setAdslTransactions(adslTransactionsData || []);
    } catch (error) {
      console.error('📊 History - Error fetching history data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const clearDateFilter = () => {
    setDate(undefined);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex justify-between items-center">
            <span>{t('common.history')}</span>
            <div className="flex items-center space-x-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 gap-1">
                    <Calendar className="h-4 w-4" />
                    {date ? format(date, 'dd/MM/yyyy') : <span>{t('common.filterByDate')}</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <CalendarComponent
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
              {date && (
                <Button variant="ghost" size="sm" onClick={clearDateFilter} className="h-8">
                  {t('common.clear')}
                </Button>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-grow overflow-hidden">
          <HistoryTabs
            isLoading={isLoading}
            date={date}
            selections={selections}
            transfers={transfers}
            recharges={recharges}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
